import { BehaviorSubject, combineLatest, Observable, of as observableOf, Subscription } from 'rxjs';

import { Injectable } from '@angular/core';
import { PagedResponse, SnapshotWidget } from '@vendasta/snapshot-widget';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { Salesperson } from '../core/salesperson';
import { SalespersonService } from '../core/salesperson.service';
import { SnapshotWidgetApiService } from './snapshot-widget.api.service';

export class AcquisitionWidgetNotification {
  text: string;
  error: boolean;

  constructor(text: string, error: boolean) {
    this.text = text;
    this.error = error;
  }
}

@Injectable()
export class AcquisitionWidgetStoreService {
  private widgetsSubject: BehaviorSubject<SnapshotWidget[]> = new BehaviorSubject(null);
  private nextCursorSubject: BehaviorSubject<string> = new BehaviorSubject('');
  private hasMoreSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private loadingMoreSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private notification: BehaviorSubject<AcquisitionWidgetNotification> = new BehaviorSubject(null);
  private subscriptions: Subscription[] = [];
  private selectedMarketId$$: BehaviorSubject<string | null> = new BehaviorSubject(null);

  constructor(
    private snapshotWidgetApiService: SnapshotWidgetApiService,
    private salespersonService: SalespersonService,
  ) {}

  get notification$(): Observable<AcquisitionWidgetNotification> {
    return this.notification.asObservable();
  }

  get widgets$(): Observable<SnapshotWidget[]> {
    return this.widgetsSubject.asObservable();
  }

  get hasMore$(): Observable<boolean> {
    return this.hasMoreSubject.asObservable();
  }

  get loadingMore$(): Observable<boolean> {
    return this.loadingMoreSubject.asObservable();
  }

  get nextCursor$(): Observable<string> {
    return this.nextCursorSubject.asObservable();
  }

  public initializeStore(): void {
    this.notification = new BehaviorSubject<AcquisitionWidgetNotification>(null);
    this.subscriptions.push(
      this.selectedMarketId$$
        .pipe(
          switchMap((marketId) => {
            return combineLatest([
              this.snapshotWidgetApiService.listSnapshotWidgets(marketId),
              this.salespersonService.loadAllSalespeopleAjax(),
            ]);
          }),
          catchError((err) => {
            this.notification.next(this.buildNotification(err.message, true));
            return observableOf(null);
          }),
        )
        .subscribe(([response, salespeople]) => this.handleApiResponse(response, salespeople)),
    );
    this.subscriptions.push(this.selectedMarketId$$.subscribe(() => this.widgetsSubject.next(null)));
  }

  public loadMoreWidgets(): void {
    this.loadingMoreSubject.next(true);
    this.hasMoreSubject.next(false);
    combineLatest([this.selectedMarketId$$, this.nextCursorSubject])
      .pipe(
        switchMap(([marketId, cursor]) => this.snapshotWidgetApiService.listSnapshotWidgets(marketId, cursor)),
        take(1),
        finalize(() => this.loadingMoreSubject.next(false)),
      )
      .subscribe(
        (response) => this.handleApiResponse(response, null),
        () => this.notification.next(this.buildNotification('Failed to load widgets', true)),
      );
  }

  public getSalespeople(ids: string[] = []): { displayName: string; names: string[] } {
    if (ids.length === 0) {
      return {
        displayName: 'All',
        names: [],
      };
    }
    if (ids.length > 1) {
      const names = [];
      ids.forEach((id) => {
        const thisSalesperson = this.salespersonService.getSalespersonFromCache(id);
        if (thisSalesperson) {
          names.push(thisSalesperson.name);
        }
      });
      return {
        displayName: 'Multiple',
        names: names,
      };
    }
    const salesperson = this.salespersonService.getSalespersonFromCache(ids[0]);
    return {
      displayName: salesperson ? salesperson.name : 'Unknown',
      names: [],
    };
  }

  public deleteWidget(widget: SnapshotWidget): void {
    this.snapshotWidgetApiService.deleteSnapshotWidget(widget.widgetId).subscribe(
      () => {
        this.notification.next(
          this.buildNotification(
            `${widget.name} has been deleted successfully.
        Ensure that you delete the embed code from your website.`,
            false,
          ),
        );
        this.widgetsSubject.next(this.widgetsSubject.getValue().filter((sw) => sw.widgetId !== widget.widgetId));
      },
      (error) => {
        if (error.statusCode === 404) {
          this.notification.next(this.buildNotification(`${widget.name} could not be found`, true));
        } else {
          this.notification.next(this.buildNotification('Failed to delete widget', true));
        }
      },
    );
  }

  public updateWidget(widget: SnapshotWidget): void {
    this.snapshotWidgetApiService.updateSnapshotWidget(widget).subscribe(
      () => {
        this.notification.next(this.buildNotification(`${widget.name} has been updated successfully.`, false));
      },
      (error) => {
        if (error.statusCode === 404) {
          this.notification.next(this.buildNotification(`${widget.name} could not be found`, true));
        } else {
          this.notification.next(this.buildNotification('Failed to update widget', true));
        }
      },
    );
  }

  public selectMarket(marketId: string | null): void {
    this.selectedMarketId$$.next(marketId);
  }

  private handleApiResponse(response: PagedResponse<SnapshotWidget>, salespeople: Salesperson[]): void {
    if (salespeople) {
      this.salespersonService.updateSalespeople(salespeople);
    }
    if (response) {
      const currentWidgets = this.widgetsSubject.getValue() || [];
      this.widgetsSubject.next(currentWidgets.concat(response.results || []));
      this.hasMoreSubject.next(response.hasMore || false);
      this.nextCursorSubject.next(response.nextCursor || '');
    }
  }

  private buildNotification(text: string, error: boolean): AcquisitionWidgetNotification {
    return new AcquisitionWidgetNotification(text, error);
  }

  public destroyStore(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.notification.complete();
    this.widgetsSubject.next(null);
  }
}
